import React, { useState, useEffect } from 'react';
import { X, FileText, Eye } from 'lucide-react';
import { generateLeasePDF } from '../utils/leasePdfGenerator';

interface LeasePreviewProps {
  content: {
    coverPage: string;
    mainContent: string;
  };
  isOpen: boolean;
  onClose: () => void;
  leaseData?: any;
}

const LeasePreview: React.FC<LeasePreviewProps> = ({ content, isOpen, onClose, leaseData }) => {
  const [previewMode, setPreviewMode] = useState<'text' | 'pdf'>('text');
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (previewMode === 'pdf' && leaseData) {
      generatePdfPreview();
    }
    // Cleanup on unmount or when preview mode changes
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl('');
      }
    };
  }, [previewMode, leaseData]);

  const generatePdfPreview = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Create a preview version of the lease data
      const previewData = {
        ...leaseData,
        signature: '', // Empty signature for preview
        acceptanceDate: new Date().toISOString(),
      };

      const pdfBlob = await generateLeasePDF(content, previewData);
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
      } else {
        throw new Error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      setError('Failed to generate PDF preview. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Lease Agreement Preview
            </h2>
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setPreviewMode('text')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  previewMode === 'text'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4 inline-block mr-2" />
                Text
              </button>
              <button
                onClick={() => setPreviewMode('pdf')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  previewMode === 'pdf'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Eye className="w-4 h-4 inline-block mr-2" />
                PDF
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {previewMode === 'text' ? (
            <div className="prose dark:prose-invert max-w-none">
              <div className="mb-8 pb-8 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4">Cover Page</h3>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {content.coverPage}
                </pre>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Agreement Content</h3>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {content.mainContent}
                </pre>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[600px] bg-gray-100 dark:bg-gray-700 rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                  <p className="text-red-500 mb-2">{error}</p>
                  <button
                    onClick={generatePdfPreview}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : pdfUrl ? (
                <object
                  data={pdfUrl}
                  type="application/pdf"
                  className="w-full h-full min-h-[600px] rounded-lg"
                >
                  <embed
                    src={pdfUrl}
                    type="application/pdf"
                    className="w-full h-full min-h-[600px] rounded-lg"
                  />
                </object>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No PDF preview available
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeasePreview;